// src/pages/onboarding/results.tsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  BarChart2,
  Search,
  Bot,
  ChevronsRight,
  Bug,
  RotateCcw,
  Tags,
} from "lucide-react";
import { useOnboarding } from "@/contexts/onboarding-context";

const DEBUG = process.env.NODE_ENV === "development";

const ResultsCard: React.FC<{
  title: string;
  description: string;
  icon: React.ReactNode;
  link: string;
  isGated?: boolean;
}> = ({ title, description, icon, link, isGated = false }) => {
  const { isSignedIn } = useUser();
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent) => {
    if (isGated && !isSignedIn) {
      e.preventDefault();
      navigate("/sign-up-gate", { state: { redirectTo: link } });
    }
  };

  const content = (
    <Card className="h-full bg-gradient-card backdrop-blur-sm border border-border/50 shadow-large hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer">
      <CardContent className="flex flex-col items-center text-center p-8">
        <div className="mb-6">{icon}</div>
        <CardTitle className="text-xl font-semibold mb-4">{title}</CardTitle>
        <CardDescription className="text-base leading-relaxed mb-6">
          {description}
        </CardDescription>
        <div className="flex items-center gap-2 text-primary font-medium">
          <span>Get Started</span>
          <ChevronsRight className="w-4 h-4" />
        </div>
      </CardContent>
    </Card>
  );

  if (isGated && !isSignedIn) {
    return <div onClick={handleClick}>{content}</div>;
  }

  return <Link to={link}>{content}</Link>;
};

export default function OnboardingResultsPage() {
  const { setStage } = useOnboarding();
  const navigate = useNavigate();
  const [isDebugMode, setIsDebugMode] = useState(false);

  // Get debug mode from localStorage
  useEffect(() => {
    const debugMode = localStorage.getItem("onboarding-debug-mode") === "true";
    setIsDebugMode(debugMode);
  }, []);

  useEffect(() => {
    setStage("CHOOSE");
  }, [setStage]);

  const goToStart = () => {
    navigate("/onboarding/start");
  };

  return (
    <div className="min-h-screen bg-gradient-hero relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-float" />
        <div
          className="absolute bottom-0 right-1/4 w-80 h-80 bg-secondary/5 rounded-full blur-3xl animate-float"
          style={{ animationDelay: "1s" }}
        />
        <div
          className="absolute top-1/2 left-0 w-64 h-64 bg-success/5 rounded-full blur-3xl animate-float"
          style={{ animationDelay: "2s" }}
        />
      </div>

      <div className="relative z-10 px-6 py-16">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 animate-fade-in">
            <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-4 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
              Your Account Analysis is
              <span className="bg-gradient-primary bg-clip-text text-transparent">
                {" "}
                Complete!
              </span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Choose your next step to dive deeper into your Google Ads
              optimization opportunities.
            </p>
          </div>

          {/* Video Section */}
          <div
            className="mb-16 animate-scale-in text-center"
            style={{ animationDelay: "150ms" }}
          >
            <h2 className="text-2xl font-semibold mb-2">Next Steps Overview</h2>
            <p className="text-lg text-muted-foreground mb-6">
              Watch this quick overview of your analysis options
            </p>
            <div
              className="relative w-full max-w-4xl mx-auto"
              style={{ paddingBottom: "56.25%" }}
            >
              <iframe
                src="https://www.loom.com/embed/243d67c1e0f548c48c3fd51de8ffb117?sid=a9b8c7d6-e5f4-3a2b-1c9d-8e7f6a5b4c3d"
                frameBorder="0"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full rounded-lg"
                title="Analysis Options Overview"
              ></iframe>
            </div>
          </div>

          {/* Options Grid */}
          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16 animate-fade-in"
            style={{ animationDelay: "200ms" }}
          >
            <div style={{ animationDelay: "300ms" }}>
              <ResultsCard
                title="Explore Quick Wins"
                description="Dive into your complete account dashboard with detailed performance insights, trend analysis, and optimization opportunities."
                icon={<BarChart2 className="w-12 h-12 text-primary" />}
                link="/onboarding/quick-wins"
              />
            </div>

            <div style={{ animationDelay: "400ms" }}>
              <ResultsCard
                title="Keyword Viewer"
                description="Analyze keyword performance, identify top contributors, and find optimization opportunities."
                icon={<Tags className="w-12 h-12 text-primary" />}
                link="/onboarding/keywords"
              />
            </div>

            <div style={{ animationDelay: "500ms" }}>
              <ResultsCard
                title="Search Term Viewer"
                description="Discover high-performing search terms and identify negative keyword opportunities to optimize your campaigns."
                icon={<Search className="w-12 h-12 text-primary" />}
                link="/onboarding/search-terms"
              />
            </div>

            <div style={{ animationDelay: "600ms" }}>
              <ResultsCard
                title="Advanced AI Insights"
                description="Get deeper AI-powered analysis and recommendations tailored to your specific account performance and goals."
                icon={<Bot className="w-12 h-12 text-primary" />}
                link="/onboarding/ai-setup"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Debug Panel */}
      {DEBUG && (
        <div className="fixed bottom-4 right-4 z-50 animate-fade-in">
          <Card className="bg-gradient-card backdrop-blur-sm border border-border/50 shadow-large">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <Bug className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Debug Panel</span>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={isDebugMode}
                    onCheckedChange={(checked) => {
                      setIsDebugMode(checked);
                      localStorage.setItem(
                        "onboarding-debug-mode",
                        checked.toString(),
                      );
                    }}
                    id="debug-mode"
                  />
                  <label htmlFor="debug-mode" className="text-sm">
                    Debug Mode
                  </label>
                </div>
                <Button
                  onClick={goToStart}
                  variant="outline"
                  size="sm"
                  className="text-xs w-full"
                >
                  <RotateCcw className="h-3 w-3 mr-1" />
                  Restart Onboarding
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
